import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
//components
import Button from "components/Button";
import Message from "components/Message";
import Container from "components/Container";
import Loading from "components/Loading";
//hooks
import { useSuppliers } from "hooks/api/useSuppliers";
import { useSupplierBusinessTypes } from "hooks/api/useSupplierBusinessTypes";
import { useSupplierCountries } from "hooks/api/useSupplierCountries";
import {
  useSupplierCategories,
  useSupplierSubCategories,
} from "hooks/api/useSupplierCategories";
import CTAHero from "features/home/components/CTAHero";

const schema = yup.object({
  businessType: yup.string().required("Business Type is required"),
  registrationNumber: yup.string().required("Registration Number is required"),
  vatNumber: yup.string().required("VAT is required"),
  companyName: yup.string().required("Company Name is required"),
  primaryContactFullName: yup
    .string()
    .required("Primary Contact FullName is required"),
  primaryContactDesignation: yup
    .string()
    .required("Primary Contact Designation is required"),
  primaryContactPhone: yup
    .string()
    .required("Primary Contact Phone is required"),
  primaryContactEmail: yup
    .string()
    .email("Email must be a valid format")
    .required("Primary Contact Email is required"),
  primaryContactAddress1: yup
    .string()
    .required("Primary Contact Address1 is required"),
  primaryContactAddress2: yup
    .string()
    .required("Primary Contact Address2 is required"),
  primaryContactProvince: yup
    .string()
    .required("Primary Contact Province is required"),
  primaryContactCity: yup.string().required("Primary Contact City is required"),
  primaryContactPostalCode: yup
    .string()
    .required("Primary Contact PostalCode is required"),
  primaryContactCountry: yup
    .string()
    .required("Primary Contact Country is required"),
  category: yup.string().required("Category is required"),
  subCategory: yup.string().required("Sub Category is required"),
  companyContactFullName: yup
    .string()
    .required("Company Contact FullName is required"),
  companyContactDesignation: yup
    .string()
    .required("Company Contact Designation is required"),
  companyContactPhone: yup
    .string()
    .required("Company Contact Phone is required"),
  companyContactEmail: yup
    .string()
    .email("Email must be a valid format")
    .required("Company Contact Email is required"),
  companyContactAddress1: yup
    .string()
    .required("Company Contact Address1 is required"),
  companyContactAddress2: yup
    .string()
    .required("Company Contact Address2 is required"),
  companyContactProvince: yup
    .string()
    .required("Company Contact Province is required"),
  companyContactCity: yup.string().required("Company Contact City is required"),
  companyContactPostalCode: yup
    .string()
    .required("Company Contact Postal Code is required"),
  companyContactCountry: yup
    .string()
    .required("Company Contact Country is required"),
});

export default function SupplierRegistrationCorporate() {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const { supplierCategories } = useSupplierCategories();
  const { supplierSubCategories, setParentId } = useSupplierSubCategories();
  const { data } = useSupplierBusinessTypes();
  const supplierBusinessTypes = data?.data;
  const { countries } = useSupplierCountries();
  const { isLoading, createCorporateSupplier } = useSuppliers();

  useEffect(() => {
    if (supplierBusinessTypes?.length > 0) {
      setValue("businessType", supplierBusinessTypes[0].name);
    }
  }, [supplierBusinessTypes, setValue]);

  useEffect(() => {
    if (countries?.length > 0) {
      setValue("primaryContactCountry", countries[0].label);
      setValue("companyContactCountry", countries[0].label);
    }
  }, [countries, setValue]);

  useEffect(() => {
    if (supplierCategories?.length > 0) {
      setValue("category", supplierCategories[0].id);
      setParentId(supplierCategories[0].id);
    }
  }, [supplierCategories, setValue, setParentId]);

  useEffect(() => {
    if (supplierSubCategories?.length > 0) {
      setValue("subCategory", supplierSubCategories[0].id);
    }
  }, [supplierSubCategories, setValue]);

  const onSubmit = (values) => {
    try {
      createCorporateSupplier({
        business_type: values.businessType,
        registration_number: values.registrationNumber,
        vat_number: values.vatNumber,
        company_name: values.companyName,
        primary_contact_full_name: values.primaryContactFullName,
        primary_contact_designation: values.primaryContactDesignation,
        primary_contact_phone: values.primaryContactPhone,
        primary_contact_email: values.primaryContactEmail,
        primary_contact_address1: values.primaryContactAddress1,
        primary_contact_address2: values.primaryContactAddress2,
        primary_contact_province: values.primaryContactProvince,
        primary_contact_city: values.primaryContactCity,
        primary_contact_postal_code: values.primaryContactPostalCode,
        primary_contact_country: values.primaryContactCountry,
        supplier_category_id: values.category,
        supplier_sub_category_id: values.subCategory,
        company_contact_full_name: values.companyContactFullName,
        company_contact_designation: values.companyContactDesignation,
        company_contact_phone: values.companyContactPhone,
        company_contact_email: values.companyContactEmail,
        company_contact_address1: values.companyContactAddress1,
        company_contact_province: values.companyContactProvince,
        company_contact_city: values.companyContactCity,
        company_contact_postal_code: values.companyContactPostalCode,
        company_contact_country: values.companyContactCountry,
        company_contact_address2: values.companyContactAddress2,
      });
    } catch (err) {}
  };

  return (
    <div>
      <CTAHero />
      <Container className="mt-8">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 border border-slate-200 rounded-md max-w-5xl mx-auto px-12"
        >
          <div className="mt-6 flex items-center justify-between gap-x-6">
            <h3 className="text-2xl font-bold">Create Supplier - Corporate</h3>
            <div className="flex gap-4">
              <Button disabled={isLoading} type="submit">
                <Loading text="Register" isLoading={isLoading} />
              </Button>
              <Button
                href="/supplier-register"
                color="gray"
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                Cancel
              </Button>
            </div>
          </div>

          <div className="border-b border-purple-200"></div>

          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label
                htmlFor="business-type"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Business Type<span className="text-red-600">*</span>
              </label>
              <div className="mt-2">
                <select
                  id="business-type"
                  name="business-type"
                  {...register("businessType")}
                  autoComplete="off"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                >
                  {supplierBusinessTypes?.map((businessType) => (
                    <option key={businessType.id} value={businessType.name}>
                      {businessType.name}
                    </option>
                  ))}
                </select>
                <Message message={errors.businessType?.message} />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label
                htmlFor="registration-number"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Company Registration Number
                <span className="text-red-600">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="registration-number"
                  name="registration-number"
                  type="text"
                  {...register("registrationNumber")}
                  autoComplete="off"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                />
                <Message message={errors.registrationNumber?.message} />
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <label
                htmlFor="company-name"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Company Name<span className="text-red-600">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="company-name"
                  name="company-name"
                  type="text"
                  {...register("companyName")}
                  autoComplete="off"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                />
                <Message message={errors.companyName?.message} />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="vat"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                VAT Number<span className="text-red-600">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="vat"
                  name="vat"
                  type="number"
                  {...register("vatNumber")}
                  autoComplete="off"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                />
                <Message message={errors.vatNumber?.message} />
              </div>
            </div>
          </div>

          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Primary Contact Person<span className="text-red-600">*</span>
            </h2>

            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="primary-full-name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  First name<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-full-name"
                    name="primary-full-name"
                    type="text"
                    {...register("primaryContactFullName")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactFullName?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="designation"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Designation<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="designation"
                    name="designation"
                    type="text"
                    {...register("primaryContactDesignation")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message
                    message={errors.primaryContactDesignation?.message}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="primary-phone"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Phone Number<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-phone"
                    name="primary-phone"
                    type="tel"
                    {...register("primaryContactPhone")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactPhone?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="primary-email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Email address<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-email"
                    name="primary-email"
                    type="text"
                    {...register("primaryContactEmail")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactEmail?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="primary-address-1"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Address Line 1<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-address-1"
                    name="primary-address-1"
                    type="text"
                    {...register("primaryContactAddress1")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactAddress1?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="primary-address-2"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Address Line 2<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-address-2"
                    name="primary-address-2"
                    type="text"
                    {...register("primaryContactAddress2")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactAddress2?.message} />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="primary-region"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Region / Province<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-region"
                    name="primary-region"
                    type="text"
                    {...register("primaryContactProvince")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactProvince?.message} />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="primary-city"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  City<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-city"
                    name="primary-city"
                    type="text"
                    {...register("primaryContactCity")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactCity?.message} />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="primary-postal-code"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Postal Code<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="primary-postal-code"
                    name="primary-postal-code"
                    type="text"
                    {...register("primaryContactPostalCode")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.primaryContactPostalCode?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="primary-country"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Country<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <select
                    id="primary-country"
                    name="primary-country"
                    autoComplete="off"
                    {...register("primaryContactCountry")}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  >
                    {countries?.map((country) => (
                      <option key={country.value} value={country.label}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  <Message message={errors.primaryContactCountry?.message} />
                </div>
              </div>

              <div className="divide-y"></div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="categories"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Supplier Category<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <select
                    id="categories"
                    name="categories"
                    {...register("category", {
                      onChange: (e) => {
                        const value = e.target.value;
                        setParentId(value);
                      },
                    })}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  >
                    {supplierCategories?.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <Message message={errors.category?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="sub-category"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Supplier Sub-Category<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <select
                    id="sub-category"
                    name="sub-category"
                    {...register("subCategory")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  >
                    {supplierSubCategories?.map((subCategory) => (
                      <option key={subCategory.id} value={subCategory.id}>
                        {subCategory.name}
                      </option>
                    ))}
                  </select>
                  <Message message={errors.subCategory?.message} />
                </div>
              </div>

              <div className="divide-y"></div>
            </div>
          </div>

          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Company Contact Person
            </h2>

            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="company-full-name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Full Name<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-full-name"
                    name="company-full-name"
                    type="text"
                    {...register("companyContactFullName")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactFullName?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="company-designation"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Designation<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-designation"
                    name="company-designation"
                    type="text"
                    {...register("companyContactDesignation")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message
                    message={errors.companyContactDesignation?.message}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="company-phone"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Phone Number<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-phone"
                    name="company-phone"
                    type="tel"
                    {...register("companyContactPhone")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactPhone?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="company-email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Email address<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-email"
                    name="company-email"
                    type="text"
                    {...register("companyContactEmail")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactEmail?.message} />
                </div>
              </div>

              <div className="sm:col-span-full">
                <div className="relative flex gap-x-3">
                  <div className="flex h-6 items-center">
                    <input
                      id="same-office-address"
                      name="same-office-address"
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-600"
                    />
                  </div>
                  <div className="text-sm leading-6">
                    <label
                      htmlFor="same-office-address"
                      className="font-medium text-gray-900"
                    >
                      Same With office address
                    </label>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="company-address-1"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Address Line 1<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-address-1"
                    name="company-address-1"
                    type="text"
                    {...register("companyContactAddress1")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactAddress1?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="company-address-2"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Address Line 2<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-address-2"
                    name="company-address-2"
                    type="text"
                    {...register("companyContactAddress2")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactAddress2?.message} />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="company-region"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Region / Province<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-region"
                    name="company-region"
                    type="text"
                    {...register("companyContactProvince")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactProvince?.message} />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="company-city"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  City<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-city"
                    name="company-city"
                    type="text"
                    {...register("companyContactCity")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactCity?.message} />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="company-postal-code"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Postal Code<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="company-postal-code"
                    name="company-postal-code"
                    type="text"
                    {...register("companyContactPostalCode")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.companyContactPostalCode?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="company-country"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Country<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <select
                    id="company-country"
                    name="company-country"
                    {...register("companyContactCountry")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  >
                    {countries?.map((country) => (
                      <option key={country.value} value={country.label}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  <Message message={errors.companyContactCountry?.message} />
                </div>
              </div>
            </div>
          </div>
        </form>
      </Container>
    </div>
  );
}
